import {
  AssessmentGroupAnonymity,
  AssessmentGroupProviders,
  AssessmentQuestionComment,
  AssessmentQuestionResponseVisibility,
  AssessmentQuestionResponses,
  CompetencyAssessmentType,
  ComplianceProgramAppliesTo,
  ComplianceProgramCalibrationType,
  ComplianceProgramRecurrence,
  ComplianceProgramState,
  ComplianceProgramStep,
  ProgramScheduledReminderInput,
} from "types/graphql-schema";

export enum ProgramFormTab {
  Workflow,
  Questions,
  Participants,
  Notifications,
}

export enum ComplianceProgramType {
  REVIEW = "review",
  ENGAGEMENT_SURVEY = "survey",
  ONE_ON_ONE = "one_on_one",
}

export enum ProgramStartTrigger {
  ON_START_DATE,
  EMPLOYEE_START_DATE,
}

export enum ProgramEvaluationPeriodType {
  PAST_WEEK,
  PAST_TWO_WEEKS,
  PAST_MONTH,
  PAST_TWO_MONTHS,
  PAST_QUARTER,
  PAST_HALF_YEAR,
  PAST_YEAR,
  CUSTOM,
}

export type ProgramFormTalentIndicatorReviewConfig = {
  sectionTitle: string;
  sectionDescription: string;
  talentIndicators: {
    talentIndicatorId: number;
    responses: AssessmentQuestionResponses;
    responseVisibility: AssessmentQuestionResponseVisibility;
    comment: AssessmentQuestionComment;
    description: string;
  }[];
};
export type ProgramFormRoleBasedReviewConfig = {
  enabledForCurrentRole: boolean;
  enabledForNextRole: boolean;
  enabledSelfReviewForNextRole: boolean;
  competencyAssessmentType: CompetencyAssessmentType;
  nextRoleCompetencyAssessmentType: CompetencyAssessmentType;
  assessmentScale: number;
  assessmentScaleStartsAt: number;
  assessmentScaleLabels: string[];
};
export type ProgramFormGoalReviewConfig = {
  enabled: boolean;
  assessmentScale: number;
  assessmentScaleStartsAt: number;
  assessmentScaleLabels: string[];
  responses: AssessmentQuestionResponses;
};
export type ProgramFormCoreValueReviewConfig = {
  enabled: boolean;
  assessmentScale: number;
  assessmentScaleStartsAt: number;
  assessmentScaleLabels: string[];
  responses: AssessmentQuestionResponses;
};

export type ProgramFormStepSurvey = {
  stepType: ComplianceProgramStep.EngagementSurvey;
  templateId?: number;
  questionSetIds: number[];
  anonymity: AssessmentGroupAnonymity;
};
type ProgramFormStepPreCalibration = {
  stepType: ComplianceProgramStep.PreCalibration;
};
type ProgramFormStepSelfReview = {
  stepType: ComplianceProgramStep.SelfReview;
  dueDateOffset?: string | null;
};
export type ProgramFormStepPeerReview = {
  stepType: ComplianceProgramStep.PeerReview;
  templateId?: number;
  dueDateOffset?: string | null;
  questionSetIds: number[];
  providers: AssessmentGroupProviders;
  anonymity: AssessmentGroupAnonymity;
  roleBasedReviewConfig: ProgramFormRoleBasedReviewConfig;
  goalReviewConfig: ProgramFormGoalReviewConfig;
  coreValueReviewConfig: ProgramFormCoreValueReviewConfig;
};
export type ProgramFormStepUpwardReview = {
  stepType: ComplianceProgramStep.UpwardReview;
  templateId?: number;
  dueDateOffset?: string | null;
  questionSetIds: number[];
  anonymity: AssessmentGroupAnonymity;
  talentIndicatorReviewConfig: ProgramFormTalentIndicatorReviewConfig;
};
export type ProgramFormStepDownwardReview = {
  stepType: ComplianceProgramStep.DownwardReview;
  templateId?: number;
  dueDateOffset?: string | null;
  questionSetIds: number[];
  roleBasedReviewConfig: ProgramFormRoleBasedReviewConfig;
  goalReviewConfig: ProgramFormGoalReviewConfig;
  coreValueReviewConfig: ProgramFormCoreValueReviewConfig;
  talentIndicatorReviewConfig: ProgramFormTalentIndicatorReviewConfig;
};
type ProgramFormStepPostCalibration = {
  stepType: ComplianceProgramStep.PostCalibration;
};
type ProgramFormStepDelivery = {
  stepType: ComplianceProgramStep.Delivery;
  dueDateOffset?: string | null;
};
type ProgramFormStepOneOnOne = {
  stepType: ComplianceProgramStep.OneOnOne;
  dueDateOffset?: string | null;
  topicTemplateId?: number;
};
export type ProgramFormStep =
  | ProgramFormStepPreCalibration
  | ProgramFormStepSelfReview
  | ProgramFormStepPeerReview
  | ProgramFormStepUpwardReview
  | ProgramFormStepDownwardReview
  | ProgramFormStepPostCalibration
  | ProgramFormStepDelivery
  | ProgramFormStepOneOnOne
  | ProgramFormStepSurvey;

export type ProgramForm = {
  id?: number;
  organizationId: number;
  title: string;
  ongoing: boolean;
  state: ComplianceProgramState;
  recurrence: ComplianceProgramRecurrence;
  startDate?: string;
  dueDate?: string;
  startDateOffset?: number;
  startDateDueDateOffset?: number;
  evaluationPeriod: {
    type: ProgramEvaluationPeriodType;
    startDate?: string;
    endDate?: string;
  };
  startDatePeriodStartOffset?: number;
  startDatePeriodEndOffset?: number;
  sharesWithSubject: boolean;
  calibration: ComplianceProgramCalibrationType;
  appliesTo: ComplianceProgramAppliesTo;
  appliesToTeams: { id: number; title: string }[];
  appliesToManagers: { id: number; name: string }[];
  appliesToUsers: { id: number; name: string }[];
  excludedUsers: { id: number; name: string }[];
  excludedHireDateAfter?: string;
  scheduledReminders: ProgramScheduledReminderInput[];
  steps: ProgramFormStep[];
};
